import React, { useMemo } from 'react';
import DataSection from './DataSection';
import styles from '../TradeFlowTrackingTab.module.css';
import {
  ScoringGateDecision,
  ScoringGateDecisionsParams,
  getScoringDecisions,
} from '../../../services/tradeFlowAdminApi';
import { useFlowData } from '../hooks/useTradeFlowData';

interface ScoringSectionProps {
  dateFrom: string;
  dateTo: string;
  symbol: string;
}

const ScoringSection: React.FC<ScoringSectionProps> = ({ dateFrom, dateTo, symbol }) => {
  const [showPassed, setShowPassed] = React.useState<string>('all');

  const params = useMemo<ScoringGateDecisionsParams>(() => ({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
    symbol: symbol || undefined,
    passed: showPassed === 'all' ? undefined : showPassed === 'passed',
  }), [dateFrom, dateTo, symbol, showPassed]);

  const { data, total, page, setPage, isLoading, isFetching, isError, error, totalPages } =
    useFlowData<ScoringGateDecision, ScoringGateDecisionsParams>({
      queryKeyBase: ['admin', 'trade-flow', 'scoring'],
      fetchFn: (p) => getScoringDecisions(p).then((r) => r),
      params,
      refetchInterval: 10000,
    });

  return (
    <DataSection
      title="Scoring & Gates"
      tabNumber={5}
      description="Composite scoring and pre-pool entry gates. These combine liquidity, volatility, technical, sentiment, structure, timing, and memory scores before recommendations enter the Smart Pool."
      loading={isLoading}
      isFetching={isFetching}
      error={error?.message || null}
      dataCount={data.length}
      page={page}
      totalPages={totalPages}
      total={total}
      onPageChange={setPage}
    >
      <div className={styles.filterBar}>
        <select
          value={showPassed}
          onChange={(e) => { setShowPassed(e.target.value); setPage(1); }}
          className={styles.filterSelect}
        >
          <option value="all">All Results</option>
          <option value="passed">Passed Only</option>
          <option value="failed">Failed Only</option>
        </select>
      </div>

      <table className={styles.dataTable}>
        <thead>
          <tr>
            <th>Time</th>
            <th>Symbol</th>
            <th>Recommendation ID</th>
            <th>Filter</th>
            <th>Result</th>
            <th>Score/Value</th>
            <th>Detail</th>
          </tr>
        </thead>
        <tbody>
          {data.map((d) => (
            <tr key={d.id}>
              <td className={styles.monoSmall}>
                {d.evaluated_at ? new Date(d.evaluated_at).toLocaleString() : '-'}
              </td>
              <td className={styles.mono}>{d.symbol}</td>
              <td className={styles.monoSmall}>{d.recommendation_id}</td>
              <td>{d.filter_name}</td>
              <td>
                <span className={d.passed ? styles.badgePass : styles.badgeFail}>
                  {d.passed ? 'PASS' : 'FAIL'}
                </span>
              </td>
              <td className={styles.mono}>
                {d.actual_value != null ? d.actual_value.toFixed(4) : '-'}
              </td>
              <td className={styles.detailCell}>{d.detail || '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </DataSection>
  );
};

export default ScoringSection;
